// Persistent reveal/expiry state for self-destructing ("timer") messages.
//
// A timed message carries `head.timer` = lifetime in seconds. It is delivered and stored
// like any other message; the *client* is responsible for hiding it. This mirrors
// ios/Tinodios/TimerRevealStore.swift so the two clients behave the same way:
//
//   - the sender always sees their own timed message (it never expires for them);
//   - a recipient sees a "tap to view" placeholder until they reveal it;
//   - once revealed, the message is visible for `timer` seconds and then hidden forever.
//
// NOTE: the server does not delete timed messages, so this is client-side hygiene only,
// not a security guarantee.

const REVEALS_KEY = 'apporigin.timerReveals';
const EXPIRED_KEY = 'apporigin.timerExpired';
// Drop bookkeeping older than 30 days.
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
// Cap the expired list so localStorage cannot grow without bound.
const MAX_EXPIRED = 5000;

function safeParse(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return fallback;
    }
    const val = JSON.parse(raw);
    return val && typeof val == 'object' ? val : fallback;
  } catch (err) {
    return fallback;
  }
}

function safeWrite(key, val) {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch (err) {
    // Private mode / quota exceeded: degrade to in-memory only.
  }
}

class TimerStore {
  constructor() {
    this.reveals = safeParse(REVEALS_KEY, {});
    // Stored as a map key -> timestamp so it can be pruned.
    this.expired = safeParse(EXPIRED_KEY, {});
    this.prune();
  }

  static key(topic, seq) {
    return `${topic}:${seq}`;
  }

  prune() {
    const cutoff = Date.now() - MAX_AGE_MS;
    let dirtyReveals = false, dirtyExpired = false;
    for (const k in this.reveals) {
      if (!(this.reveals[k].at > cutoff)) {
        delete this.reveals[k];
        dirtyReveals = true;
      }
    }
    const expKeys = Object.keys(this.expired);
    for (const k of expKeys) {
      if (!(this.expired[k] > cutoff)) {
        delete this.expired[k];
        dirtyExpired = true;
      }
    }
    if (Object.keys(this.expired).length > MAX_EXPIRED) {
      const sorted = Object.keys(this.expired).sort((a, b) => this.expired[a] - this.expired[b]);
      sorted.slice(0, sorted.length - MAX_EXPIRED).forEach(k => delete this.expired[k]);
      dirtyExpired = true;
    }
    if (dirtyReveals) {
      safeWrite(REVEALS_KEY, this.reveals);
    }
    if (dirtyExpired) {
      safeWrite(EXPIRED_KEY, this.expired);
    }
  }

  // Record the moment the recipient revealed the message. Idempotent: the original
  // reveal time is preserved so the countdown does not restart on re-render.
  recordReveal(topic, seq, duration, isSender) {
    const key = TimerStore.key(topic, seq);
    if (this.reveals[key]) {
      return;
    }
    this.reveals[key] = {at: Date.now(), duration: duration, sender: !!isSender};
    safeWrite(REVEALS_KEY, this.reveals);
  }

  // Returns {at, duration, sender, remaining} or null if not revealed / already expired.
  getReveal(topic, seq) {
    const key = TimerStore.key(topic, seq);
    const info = this.reveals[key];
    if (!info) {
      return null;
    }
    if (info.sender) {
      return Object.assign({remaining: Infinity}, info);
    }
    const remaining = info.duration - Math.floor((Date.now() - info.at) / 1000);
    if (remaining <= 0) {
      this.markExpired(topic, seq);
      return null;
    }
    return Object.assign({remaining: remaining}, info);
  }

  isRevealed(topic, seq) {
    return !!this.getReveal(topic, seq);
  }

  markExpired(topic, seq) {
    const key = TimerStore.key(topic, seq);
    if (this.reveals[key]) {
      delete this.reveals[key];
      safeWrite(REVEALS_KEY, this.reveals);
    }
    if (!this.expired[key]) {
      this.expired[key] = Date.now();
      safeWrite(EXPIRED_KEY, this.expired);
    }
  }

  isExpired(topic, seq) {
    return !!this.expired[TimerStore.key(topic, seq)];
  }
}

const timerStore = new TimerStore();

export default timerStore;
